/**
 * Panel de estado de cierre de cuenta.
 * Muestra estado actual, tipo, motivo, fechas y restitución.
 *
 * @taskId T466
 * @see functional/Specs/specs_cycle_4.md HU-16-FE
 */

import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Skeleton } from '@flexibilitypay/ui';

import { useClosureStatusWithPolling } from '../hooks/use-closure';
import type { ClosureStatus, ClosureStatusResponse } from '../types/closure.types';
import { CLOSURE_STATUS_CONFIG, INVOLUNTARY_REASON_LABELS } from '../types/closure.types';

export interface ClosureStatusPanelProps {
  /** ID de la cuenta */
  accountId: string;
  /** Habilitar polling automático (cada 10s por defecto) */
  enableAutoRefresh?: boolean;
  /** Callback cuando el estado cambia a CLOSED */
  onClosed?: () => void;
}

/**
 * Mapeo de colores de badge a clases de Tailwind.
 */
const BADGE_VARIANTS: Record<ClosureStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  CLOSURE_REQUESTED: 'outline',
  PENDING_FUND_TRANSFER: 'secondary',
  FUND_TRANSFERRED: 'default',
  CLOSED: 'default',
  CLOSURE_FAILED: 'destructive',
};

const BADGE_COLORS: Record<ClosureStatus, string> = {
  CLOSURE_REQUESTED: 'border-yellow-500 text-yellow-700 bg-yellow-50',
  PENDING_FUND_TRANSFER: 'border-orange-500 text-orange-700 bg-orange-50',
  FUND_TRANSFERRED: 'border-blue-500 text-blue-700 bg-blue-50',
  CLOSED: 'border-green-500 text-green-700 bg-green-50',
  CLOSURE_FAILED: 'border-red-500 text-red-700 bg-red-50',
};

/**
 * Iconos por estado.
 */
function StatusIcon({ status }: { status: ClosureStatus }) {
  switch (status) {
    case 'CLOSURE_REQUESTED':
      return <Clock className="h-5 w-5 text-yellow-600" />;
    case 'PENDING_FUND_TRANSFER':
      return <CreditCard className="h-5 w-5 text-orange-600" />;
    case 'FUND_TRANSFERRED':
      return <CheckCircle2 className="h-5 w-5 text-blue-600" />;
    case 'CLOSED':
      return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case 'CLOSURE_FAILED':
      return <XCircle className="h-5 w-5 text-red-600" />;
  }
}

/**
 * Formatea fecha ISO a formato legible.
 */
function formatDate(isoDate: string | undefined): string {
  if (!isoDate) return '-';
  try {
    return new Intl.DateTimeFormat('es-AR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(isoDate));
  } catch {
    return isoDate;
  }
}

/**
 * Formatea monto con precisión P-02.
 */
function formatMoney(amount: string | undefined): string {
  if (!amount) return '-';
  try {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `$ ${amount}`;
  }
}

/**
 * Panel de estado de cierre de cuenta.
 *
 * @rule P-02 — Formato monetario con precisión
 * @rule RULE-11 — 404 muestra mensaje informativo, no error
 * @see HU-16-FE, HU-51-BE
 */
export function ClosureStatusPanel({
  accountId,
  enableAutoRefresh = true,
  onClosed,
}: ClosureStatusPanelProps) {
  const { t } = useTranslation(['closure', 'common']);

  const {
    data: closureStatus,
    isLoading,
    error,
    refetch,
    startPolling,
    stopPolling,
    isPolling,
  } = useClosureStatusWithPolling(accountId);
  const closureState = closureStatus?.status;

  // Iniciar polling automático si está habilitado
  useEffect(() => {
    if (enableAutoRefresh && closureState && closureState !== 'CLOSED') {
      startPolling(10_000); // 10 segundos
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [enableAutoRefresh, closureState, startPolling, stopPolling]);

  // Callback cuando cambia a CLOSED
  useEffect(() => {
    if (closureState === 'CLOSED' && onClosed) {
      onClosed();
    }
  }, [closureState, onClosed]);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // ─── Loading state ────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  // ─── Error state ──────────────────────────────────────────────────────────

  if (error) {
    return (
      <Card className="border-destructive/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            {t('closure:status.errorTitle', 'Error al cargar estado')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {error.message || t('closure:status.genericError', 'No se pudo obtener el estado de cierre')}
          </p>
          <Button variant="outline" size="sm" className="mt-4" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            {t('common:retry', 'Reintentar')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // ─── No closure process (404) ─────────────────────────────────────────────

  if (!closureStatus) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            {t('closure:status.noProcessTitle', 'Sin proceso de cierre')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t(
              'closure:status.noProcessDescription',
              'Esta cuenta no tiene un proceso de cierre activo.',
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // ─── Main content ─────────────────────────────────────────────────────────

  const statusConfig = CLOSURE_STATUS_CONFIG[closureStatus.status];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <StatusIcon status={closureStatus.status} />
          {t('closure:status.title', 'Estado del cierre')}
        </CardTitle>
        <div className="flex items-center gap-2">
          {isPolling && (
            <span className="text-xs text-muted-foreground">
              {t('closure:status.autoRefresh', 'Actualización automática')}
            </span>
          )}
          <Button variant="ghost" size="icon" onClick={handleRefresh} title={t('common:refresh', 'Actualizar')}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Estado principal con badge */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">
            {t('closure:status.currentStatus', 'Estado actual:')}
          </span>
          <Badge variant="outline" className={BADGE_COLORS[closureStatus.status]}>
            {statusConfig.label}
          </Badge>
        </div>

        {/* Grid de información */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* Tipo de cierre */}
          <div>
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t('closure:status.closureType', 'Tipo de cierre')}
            </span>
            <p className="mt-1 font-medium">
              {closureStatus.type === 'VOLUNTARY'
                ? t('closure:status.voluntary', 'Voluntario')
                : t('closure:status.involuntary', 'Involuntario')}
            </p>
          </div>

          {/* Motivo (solo si involuntario) */}
          {closureStatus.type === 'INVOLUNTARY' && closureStatus.reason && (
            <div>
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {t('closure:status.reason', 'Motivo')}
              </span>
              <p className="mt-1 font-medium">{INVOLUNTARY_REASON_LABELS[closureStatus.reason]}</p>
            </div>
          )}

          {/* Fecha de solicitud */}
          <div>
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t('closure:status.requestDate', 'Fecha de solicitud')}
            </span>
            <p className="mt-1 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {formatDate(closureStatus.fechaSolicitud)}
            </p>
          </div>

          {/* Fecha de cierre (si CLOSED) */}
          {closureStatus.fechaCierre && (
            <div>
              <span className="text-xs font-medium uppercase text-muted-foreground">
                {t('closure:status.closeDate', 'Fecha de cierre')}
              </span>
              <p className="mt-1 flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                {formatDate(closureStatus.fechaCierre)}
              </p>
            </div>
          )}
        </div>

        {/* Observaciones */}
        {closureStatus.observaciones && (
          <div className="rounded-md border bg-muted/30 p-3">
            <span className="text-xs font-medium uppercase text-muted-foreground">
              {t('closure:status.observations', 'Observaciones')}
            </span>
            <p className="mt-1 text-sm">{closureStatus.observaciones}</p>
          </div>
        )}

        {/* Motivo de fallo (si CLOSURE_FAILED) */}
        {closureStatus.status === 'CLOSURE_FAILED' && closureStatus.motivoFallo && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <span className="text-xs font-medium uppercase text-destructive">
              {t('closure:status.failureReason', 'Motivo del fallo')}
            </span>
            <p className="mt-1 text-sm text-destructive">{closureStatus.motivoFallo}</p>
          </div>
        )}

        {/* Sección de restitución */}
        {closureStatus.restitucion && (
          <div className="rounded-md border bg-blue-50/50 p-4">
            <h4 className="mb-3 flex items-center gap-2 font-medium">
              <CreditCard className="h-4 w-4 text-blue-600" />
              {t('closure:status.restitutionTitle', 'Restitución de fondos')}
            </h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t('closure:status.destinationAccount', 'Cuenta destino')}
                </span>
                <p className="mt-1 font-mono text-sm">{closureStatus.restitucion.cuentaDestino}</p>
              </div>
              <div>
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t('closure:status.amount', 'Monto')}
                </span>
                <p className="mt-1 text-lg font-semibold">
                  {formatMoney(closureStatus.restitucion.monto)}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium uppercase text-muted-foreground">
                  {t('closure:status.transferStatus', 'Estado transferencia')}
                </span>
                <p className="mt-1">
                  {closureStatus.restitucion.estadoTransferencia === 'COMPLETED' && (
                    <Badge variant="default" className="bg-green-600">
                      {t('closure:status.transferCompleted', 'Completada')}
                    </Badge>
                  )}
                  {closureStatus.restitucion.estadoTransferencia === 'PENDING' && (
                    <Badge variant="outline" className="border-orange-500 text-orange-700">
                      {t('closure:status.transferPending', 'Pendiente')}
                    </Badge>
                  )}
                  {closureStatus.restitucion.estadoTransferencia === 'FAILED' && (
                    <Badge variant="destructive">
                      {t('closure:status.transferFailed', 'Fallida')}
                    </Badge>
                  )}
                </p>
              </div>
              {closureStatus.restitucion.fechaTransferencia && (
                <div>
                  <span className="text-xs font-medium uppercase text-muted-foreground">
                    {t('closure:status.transferDate', 'Fecha transferencia')}
                  </span>
                  <p className="mt-1">{formatDate(closureStatus.restitucion.fechaTransferencia)}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
